import * as React from 'react';
import {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  Key,
  ReactNode,
  useCallback,
} from 'react';
import { DataRequestVO, DataResponseVO } from '@mc/data-filter-react';
import * as Classes from '../common/classes';
import {
  AutoSizer,
  List as VirtualizedList,
  InfiniteLoader,
  SizeInfo,
  // @ts-ignore
} from 'react-virtualized';
import classNames from 'classnames';
import { InfiniteLoaderChildProps } from 'react-virtualized/dist/es/InfiniteLoader';
import { ListRowProps } from 'react-virtualized/dist/es/List';
import {
  CommonRenderer,
  IData,
  VirtualDataRef,
} from '../virtual-data/VirtualData';
import { useVirtualData } from '../virtual-data/useVirtualData';
import { Skeleton } from '../skeleton/Skeleton';
import { ListItem } from '../list/ListItem';
import { Deleted } from '../deleted/Deleted';
import { Spinner } from '../spinner/Spinner';
import { useCheckIds } from '../rows-data/useCheckIds';
import { NoData } from '../no-data/NoData';
import { CheckableType, VirtualListMode } from '../common/props';

export type RowRenderer<DATA, ID> = {
  id: ID;
  row: DATA | null | undefined;
  isFetching: boolean;
  isFetched: boolean;
  isDeleted: boolean;
  index: number;
  listItemProps: {
    key?: Key | null | undefined;
    style: CSSProperties;
    checked: boolean;
    checkable: CheckableType;
    onCheck: () => void;
  };
};

type Props<DATA extends IData<ID>, FILTER, ID> = {
  className?: string;
  mode?: VirtualListMode;
  api: (request: DataRequestVO<FILTER>) => Promise<DataResponseVO<DATA>>;
  ids?: ID[];
  getItemId?: (item: DATA) => ID;
  totalElements?: number;
  // Nadpis seznamu - ve výchozím stavu se zobrazuje velikostí cardTitle
  title?: React.ReactNode;
  checkedIds?: ID[];
  setCheckedIds?: (ids: ID[]) => void;
  rowHeight: number;
  children: (props: RowRenderer<DATA, ID>) => ReactNode;
  checkable?: CheckableType;
  header?: (props: CommonRenderer<ID>) => ReactNode;
  /**
   * Pokud je true, tak si vývojář chce sám řešit vzhled loading položky, pokud je false, tak se pro načítané položky volá render jako pro položky s daty
   */
  customFetching?: boolean;
  /**
   * Pokud je true, tak si vývojář chce sám řešit vzhled smazané položky, pokud je true, tak se pro smazané položky volá render jako pro položky s daty
   */
  customDeleted?: boolean;
};

const emptyArr: any[] = [];

// @ts-ignore
const fakesetCheckedIds = (ids: any[]) => {};

const VirtualListIn = <
  DATA extends IData<ID>,
  FILTER extends object = {},
  ID extends unknown = {}
>(
  {
    className,
    ids,
    title,
    rowHeight,
    totalElements,
    api,
    children,
    checkable,
    header,
    getItemId,
    checkedIds,
    customFetching,
    customDeleted,
    setCheckedIds,
    mode,
  }: Props<DATA, FILTER, ID>,
  ref: ForwardedRef<VirtualDataRef<ID>>
) => {
  const realIds = ids || emptyArr;
  const realTotalElements = totalElements || 0;
  const loading = !ids || totalElements === undefined || totalElements === null;

  const virtualData = useVirtualData<DATA, FILTER, ID>({
    ids: realIds,
    ref,
    api,
    getItemId,
  });

  const checkIds = useCheckIds<ID>(
    realIds,
    checkedIds || emptyArr,
    setCheckedIds || fakesetCheckedIds,
    checkable === 'radio'
  );

  const commonsProps: CommonRenderer<ID> = {
    headerProps: {
      checkedStatus: checkIds.checkedStatus,
      onToggleCheckAll: checkIds.onToggleCheckAll,
      totalElements: realTotalElements,
      checkable: checkable === undefined ? false : checkable,
      disabled: loading,
      extraProps: {
        totalElements,
        title,
      },
    },
    checkAll: checkIds.checkAll,
    // checkedIds: computeCheckedIds(realIds, checkedIds),
    checkedIds: checkedIds || [],
  };

  const renderRow = useCallback(
    ({ key, index, style }: ListRowProps) => {
      const rowRenderProps = virtualData.getRowRenderProps(index);
      rowRenderProps.onCheck = () => checkIds.onCheck(rowRenderProps.id);
      rowRenderProps.checkable = checkable;
      rowRenderProps.checked = checkIds.isChecked(rowRenderProps.id);

      const rowProps: RowRenderer<DATA, ID> = {
        id: rowRenderProps.id,
        isFetching: rowRenderProps.isFetching,
        isDeleted: rowRenderProps.isDeleted,
        isFetched: rowRenderProps.isFetched,
        row: rowRenderProps.row,
        index,
        listItemProps: {
          key: key,
          style: style,
          checkable:
            rowRenderProps.checkable === undefined
              ? false
              : rowRenderProps.checkable,
          checked: rowRenderProps.checked,
          onCheck: rowRenderProps.onCheck,
        },
      };

      if (rowProps.isFetching && !customFetching) {
        return (
          <ListItem {...rowProps.listItemProps}>
            <Skeleton row={index} />
          </ListItem>
        );
      }
      if (rowProps.isDeleted && !customDeleted) {
        return (
          <ListItem {...rowProps.listItemProps}>
            <Deleted />
          </ListItem>
        );
      }

      return children(rowProps);
    },
    [checkable, checkedIds, customDeleted, customFetching, realIds]
  );

  return (
    <div
      className={classNames(Classes.VIRTUAL_LIST, className, {
        [Classes.VIRTUAL_LIST_MODE_DEFAULT]: mode === 'default',
        [Classes.VIRTUAL_LIST_MODE_INPUT]: mode === 'input',
      })}
      style={{ position: 'relative' }}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            zIndex: 1,
          }}
        >
          <Spinner />
        </div>
      )}
      {header && (
        <div className={classNames(Classes.VIRTUAL_LIST_HEADER)}>
          {header(commonsProps)}
        </div>
      )}
      <div className={classNames(Classes.VIRTUAL_LIST_CONTENT)}>
        {realTotalElements > 0 || loading ? (
          <AutoSizer>
            {({ height, width }: SizeInfo) => (
              <InfiniteLoader
                ref={virtualData.infiniteLoaderRef}
                rowCount={realTotalElements}
                loadMoreRows={virtualData.loadMoreRows}
                isRowLoaded={virtualData.isRowLoaded}
                minimumBatchSize={1}
                threshold={15}
              >
                {({
                  onRowsRendered,
                  registerChild,
                }: InfiniteLoaderChildProps) => (
                  <VirtualizedList
                    __rerender={virtualData.renderer}
                    className={Classes.LIST}
                    ref={registerChild}
                    height={height}
                    rowCount={realTotalElements}
                    rowHeight={rowHeight}
                    rowRenderer={renderRow}
                    width={width}
                    onRowsRendered={(props) => {
                      virtualData.onSectionRendered({
                        startIndex: props.startIndex,
                        stopIndex: props.stopIndex,
                      });
                      onRowsRendered(props);
                    }}
                  />
                )}
              </InfiniteLoader>
            )}
          </AutoSizer>
        ) : (
          <NoData />
        )}
      </div>
    </div>
  );
};

export const VirtualList = forwardRef(VirtualListIn) as <
  DATA extends IData<ID>,
  FILTER extends object = {},
  ID extends unknown = {}
>(
  props: Props<DATA, FILTER, ID> & {
    ref?: React.ForwardedRef<VirtualDataRef<ID>>;
  }
) => ReturnType<typeof VirtualListIn>;
